/**
 * queryKeys.js — Single source of truth for all React Query cache keys.
 *
 * Rules:
 *   - Keys are arrays so React Query can match by prefix for bulk invalidation.
 *   - Tier-1 collection keys (e.g. QK.crmEntities()) invalidate all variant
 *     queries for that entity type via prefix matching.
 *   - Tier-2 single-entity keys are always [collection, id] so a collection
 *     invalidation also sweeps them.
 *   - Never hard-code a key string anywhere else in the codebase — always
 *     import and call a function from this file.
 */

export const QK = {
  // ── Tier 1: Collections ──────────────────────────────────────────────────
  /** All CRM entities, optionally filtered. Prefix-invalidates tier-2 as well. */
  crmEntities: (filters = {}) => ['crm-entities', filters],

  /** All horses, optionally filtered. */
  horses: (filters = {}) => ['horses', filters],

  /** All sale opportunities for a sale. */
  saleOpportunities: (saleId, filters = {}) => ['sale-opportunities', saleId, filters],

  /** Sale cards for a sale. */
  saleCards: (saleId) => ['sale-cards', saleId],

  /** Flat batch of ALL viewings for a sale (uses /sale-cards/all-viewings). */
  allViewings: (saleId) => ['sale-card-all-viewings', saleId],

  /** Vetting procedures for a sale. */
  vettingProcedures: (saleId) => ['vetting-procedures', saleId],

  /** Associations for a CRM entity. */
  associations: (entityId) => ['associations', entityId],

  /** Contact entries (email/phone/address) for a CRM entity. */
  contactEntries: (entityId) => ['contact-entries', entityId],

  /** Saved reports for the current tenant. */
  savedReports: (tenantId) => ['saved-reports', tenantId],

  /** Tenant alert preferences. */
  alertPrefs: (tenantId) => ['alert-prefs', tenantId],

  /** Tenant users list. */
  tenantUsers: (tenantId) => ['tenant-users', tenantId],

  // ── Tier 2: Single Entities ───────────────────────────────────────────────
  /** A single CRM entity by ID. */
  crmEntity: (id) => ['crm-entities', {}, id],

  /** A single horse by ID. */
  horse: (id) => ['horses', {}, id],

  /** A single sale opportunity by ID. */
  saleOpportunity: (id) => ['sale-opportunity', id],

  /** A single sale card with its embedded viewings. */
  saleCard: (id) => ['sale-card', id],

  // ── Tier 3: Derived / Aggregated ─────────────────────────────────────────
  /**
   * Viewer summary for a sale (aggregates sale-cards + vetting-procedures).
   * Invalidate when either sale-cards or vetting-procedures for that sale change.
   */
  viewerSummary: (saleId) => ['viewer-summary', saleId],

  /**
   * Activity tab data for a specific sale opportunity.
   * Key: ['opp-activity', oppId] — matches OpportunityActivityTab usage.
   */
  oppActivity: (oppId) => ['opp-activity', oppId],

  /** Viewer funnel insights for a sale. */
  funnelInsights: (saleId, activeOnly) => ['funnel-insights', saleId, activeOnly],

  // ── Tier 4: User / Session Scoped ────────────────────────────────────────
  /** Current authenticated user profile. */
  me: () => ['me'],

  /** Sale window ETA estimates (short-lived — poll every 60 s). */
  saleWindowEstimates: (saleId) => ['sale-window-estimates', saleId],

  /** Repository sync logs (system owner). */
  syncLogs: (filters = {}) => ['sync-logs', filters],

  // ── Horse Detail Tab Queries ──────────────────────────────────────────────
  /** Race/class records for a horse. */
  raceRecords: (horseId) => ['race-records', horseId],

  /** Progeny alerts for a horse. */
  progenyAlerts: (horseId) => ['progeny-alerts', horseId],

  /** Cover records for a horse (breeding). */
  coverRecords: (horseId) => ['cover-records', horseId],

  /** Offspring for a horse. */
  offspring: (horseId) => ['offspring', horseId],

  /** Ownership records — keyed by entity type + id. */
  ownershipRecords: (params = {}) => ['ownership-records', params],

  /** Syndicate ownership members for a syndicate entity. */
  syndicateOwnership: (syndicateId) => ['syndicate-ownership', syndicateId],

  /** Syndicate memberships for a regular account (owner), keyed by account id. */
  syndicateOwnershipByOwner: (accountId) => ['syndicate-ownership-by-owner', accountId],

  /** Files attached to an entity (horse files, etc.). */
  entityFiles: (params = {}) => ['entity-files', params],

  // ── Shared / Tenant-scoped ────────────────────────────────────────────────
  /** FarmFolio care catalog (tenant-scoped, shared across tabs). */
  farmfolioCatalog: () => ['farmfolio-catalog'],

  /** FarmFolio locations (tenant-scoped, shared across tabs). */
  farmfolioLocations: () => ['farmfolio-locations'],

  /** FarmFolio horse metrics (for Financials header summary). */
  farmfolioMetricsHorse: (horseId) => ['farmfolio-metrics-horse', horseId],

  /** FarmFolio horse care data (due-today, upcoming, scheduled, log). */
  farmfolioCareLog:       (horseId) => ['farmfolio-care-log', horseId],
  farmfolioScheduledCare: (horseId) => ['farmfolio-scheduled-care', horseId],
  farmfolioDueToday:      (horseId) => ['farmfolio-due-today', horseId],
  farmfolioUpcoming:      (horseId) => ['farmfolio-upcoming', horseId],

  // ── Account Detail Tab Queries ────────────────────────────────────────────
  /** Invoices + statements for an account's Financials tab. */
  accountInvoices:    () => ['invoices'],
  accountStatements:  () => ['invoices-statements'],
  accountGrossSales:  (entityId) => ['account-gross-sales', entityId],
  farmfolioMetricsAccount: (entityId) => ['farmfolio-metrics-account', entityId],

  /** Billing entities for the tenant. */
  billingEntities: () => ['billing-entities'],
  // ── Activity / Communications / Notes ────────────────────────────────────
  /** Tasks or Communications for an entity or horse. */
  activities: (params = {}) => ['activities', params],

  /** Conformation / inspection notes for a horse. */
  conformationNotes: (horseId) => ['conformation-notes', horseId],

  /** Hospitality & gifts for a CRM entity. */
  hospitality: (params = {}) => ['hospitality', params],

  /** Marketing campaigns for a CRM entity. */
  marketing: (params = {}) => ['marketing', params],

  // ── Calendar / Events ─────────────────────────────────────────────────────
  /** Calendar events for a date window. */
  calendarEvents: (startDate, endDate) => ['calendar-events', startDate, endDate],

  /** All events (unbounded) — used for the list/search view. */
  calendarEventsAll: () => ['calendar-events-all'],

  /** Current-week events for the Dashboard widget. */
  weekEvents: (weekStart, weekEnd) => ['week-events', weekStart, weekEnd],

  // ── Tasks ────────────────────────────────────────────────────────────────
  /** My tasks or department tasks, scoped by role. */
  tasksList: (scope) => ['tasks', scope],

  // ── Dashboard ─────────────────────────────────────────────────────────────
  /** Tenant dashboard (metrics, upcoming, my tasks). */
  tenantDashboard: () => ['tenant-dashboard'],

  // ── Settings (TenantAdminPage) ────────────────────────────────────────────
  tenantAdminUsers:    () => ['tenant-admin-users'],
  tenantUserCapacity:  () => ['tenant-user-capacity'],
  tenantDepartments:   () => ['tenant-departments'],
  viewerAccess:        () => ['viewer-access'],
  storageUsage:        () => ['storage-usage'],

  // ── FarmFolio Global Page ─────────────────────────────────────────────────
  farmfolioGlobal:          () => ['farmfolio-global'],
  farmfolioGlobalScheduled: () => ['farmfolio-global-scheduled'],
  farmfolioLayups:          () => ['farmfolio-layups'],

  // ── Invoice Module ────────────────────────────────────────────────────────
  /** Single invoice by ID — staleTime:0 to always reflect draft live-read values. */
  invoice:              (id)  => ['invoice', id],
  invoiceDashboard:     ()    => ['invoice-dashboard'],
  recurringInvoices:    ()    => ['recurring-invoices'],
  invoiceStandardItems: ()    => ['invoice-standard-items'],
  statements:           ()    => ['invoices-statements'],
  statement:            (id)  => ['invoices-statement', id],

  // ── Sales Module ──────────────────────────────────────────────────────────
  /** The tenant's own active sales list. */
  tenantSales:              ()          => ['tenant-sales'],
  /** A single sale's full details. */
  sale:                     (saleId)    => ['sale', saleId],
  /** Sessions/lots for a sale. */
  saleSessions:             (saleId)    => ['sale-sessions', saleId],
  /** Owner-level deliverables for a whole sale. */
  saleOwnerDeliverables:    (saleId)    => ['sale-owner-deliverables', saleId],
  /** Deliverables for a single sale opportunity. */
  saleDeliverables:         (oppId)     => ['sale-deliverables', oppId],

  // ── Sale Opportunities ────────────────────────────────────────────────────
  /** Post-sale tab data for an opportunity. */
  postSale:                 (oppId)     => ['post-sale', oppId],

  // ── Owner Portal ──────────────────────────────────────────────────────────
  ownerPortalDashboard:     ()          => ['owner-portal-dashboard'],
  ownerPortalAlerts:        ()          => ['owner-portal-alerts'],
  ownerPortalUnreadCount:   ()          => ['owner-portal-unread-count'],
  ownerPortalBuyerAcks:     ()          => ['owner-portal-buyer-acks'],
  ownerPortalQuestionnaires:()          => ['owner-portal-questionnaires'],
  ownerPortalDocs:          ()          => ['owner-portal-docs'],
  ownerPortalReports:       ()          => ['owner-portal-reports'],
  ownerPortalSyndicates:    ()          => ['owner-portal-syndicates'],
  ownerPortalFinalReports:  ()          => ['owner-portal-final-reports'],

  // ── Previously unregistered keys — now canonical ─────────────────────────
  /** Tenant metadata — shared across HorsesListPage, BreedingTab, FarmFolioTab, InvoicesListPage. */
  tenant:                   (tenantId)  => ['tenant', tenantId],

  /** All sales (including closed) for tenant — used in SaleOpportunityFormPage & SaleOpportunitiesPage. */
  allSales:                 ()          => ['all-sales'],

  /** All sale opportunities (all statuses) — flat list used in form pages. */
  allSaleOpportunities:     ()          => ['all-sale-opportunities'],

  /** Stud fees for a horse (male horses only). */
  studFees:                 (horseId)   => ['stud-fees', horseId],

  /** Breeding produce records for a mare. */
  breedingProduceRecords:   (horseId)   => ['breeding-produce-records', horseId],

  /** Horses linked to an account (for account Horses tab). */
  horsesTab:                (accountId) => ['horses-tab', accountId],

  /** Top horses for an account's Goals tab. */
  goalsTopHorses:           (accountId) => ['goals-top-horses', accountId],

  /** Deliverable selection for a specific opportunity (SelectDeliverablesPage). */
  selectDeliverables:       (oppId)     => ['select-deliverables', oppId],

  /** FarmFolio all-data bundle for a horse (FarmFolioTab). */
  farmfolioAll:             (horseId)   => ['farmfolio-all', horseId],

  /** User profile data for the current user. */
  userProfile:              ()          => ['user-profile'],

  /** Chatbot support tickets for the current user. */
  myTickets:                ()          => ['my-tickets'],

  /** Syndicates managed by this tenant. */
  managedSyndicates:        ()          => ['managed-syndicates'],

  /** Single managed syndicate detail. */
  syndicateDetail:          (id)        => ['syndicate-detail', id],

  /** Viewer summary for a sale (ViewerSummaryPage). */
  viewerSummaryData:        (saleId)    => ['viewer-summary-data', saleId],

  /** Contract / sale authorization document for signing. */
  contractDoc:              (id)        => ['contract-doc', id],

  /** Single saved report by ID. */
  savedReport:              (id)        => ['saved-report', id],

  /** Alert preferences for the current user. */
  userAlertPrefs:           ()          => ['user-alert-prefs'],
  /** Per-sale enrichment bundle (opps + horses + owners + deliverables). */
  activeSaleEnrichment: (saleId) => ['active-sale-enrichment', saleId],

  // ── Previously unregistered inline literals — now canonical ──────────────
  /** Sidebar badge queries. */
  sidebarMsgUnread:     () => ['sidebar-msg-unread'],
  sidebarTaskCount:     () => ['sidebar-tasks-count'],
  sidebarNotifications: () => ['sidebar-notifications'],

  /** System owner admin pages. */
  systemUsers:       () => ['system-users'],
  systemDashboard:   () => ['system-dashboard'],
  adminOpenTickets:  () => ['admin-open-tickets'],
  salesManagement:   () => ['sales-management'],
  salesManagementAll:() => ['sales-management-all'],
  monitoringData:    () => ['monitoring-data'],
  chatbotAnalytics:  () => ['chatbot-analytics'],
  saleExtensions:    () => ['sale-extensions'],
  supportTickets:    (status) => ['support-tickets', status],

  /** Statements / invoices. */
  statementEligibleEntities: () => ['statement-eligible-entities'],

  /** Sales module. */
  systemWideSales:            () => ['system-wide-sales'],
  historicSaleOpportunities:  () => ['historic-sale-opportunities'],
  saleOpportunitiesAll:       (viewHistoric) => ['sale-opportunities-all', viewHistoric],
  saleSessionsBatch:          (key) => ['sale-sessions-batch', key],

  /** Reports — prefix-only key for cross-tenant invalidation. */
  savedReportsPrefix: () => ['saved-reports'],

  /** Prefix-only key to invalidate ALL opp-activity queries for any opportunity. */
  oppActivityAll: () => ['opp-activity'],
};
